import { convertDateToText, ISODateFormat } from "@/constants";
import { getUpcomingEvents, UpcomingEvent } from "@/services/Case.service";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns/format";
import { CalendarIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import DisplayUpcomingEvent from "./dialog/DisplayUpcomingEvent";
import ColumnHeader from "./table/ColumnHeader";
import DataTable from "./table/DataTable";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Skeleton } from "./ui/skeleton";

const UpcomingEvents: React.FC<{ accessToken: string }> = ({ accessToken }) => {
	const [showEventDetails, setShowEventDetails] = useState(false);
	const [startDate, setStartDate] = useState<Date | undefined>(
		new Date(Date.now())
	);
	const [endDate, setEndDate] = useState<Date | undefined>(
		new Date(Date.now() + 604800000)
	);
	const [pickerOpen, setPickerOpen] = useState(false);
	const [secondPickerOpen, setSecondPickerOpen] = useState(false);
	const [currentEvent, setCurrentEvent] = useState<UpcomingEvent | null>(null);
	const upcomingEvents = useQuery({
		queryKey: ["upcoming_events"],
		queryFn: () =>
			getUpcomingEvents(
				accessToken,
				ISODateFormat(startDate!),
				ISODateFormat(endDate!)
			),
	});

	const displayEvent = (event: UpcomingEvent) => {
		setShowEventDetails(true);
		setCurrentEvent(event);
	};

	useEffect(() => {
		upcomingEvents.refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startDate, endDate]);

	return (
		<div className="mb-6">
			<DisplayUpcomingEvent
				open={showEventDetails}
				setOpen={setShowEventDetails}
				event={currentEvent}
			/>
			<h2 className="uppercase text-gray-700 font-bold tracking-wider">
				Upcoming Events
			</h2>
			<hr className="mb-4" />
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
				<div className="flex justify-between items-center mb-4">
					<p>Start Date:</p>
					<Popover open={pickerOpen} onOpenChange={setPickerOpen}>
						<PopoverTrigger asChild>
							<Button variant={"outline"} className="pl-3 text-left font-normal">
								{format(startDate!, "PPP")}
								<CalendarIcon className="ml-6 h-4 w-4 opacity-50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="start">
							<Calendar
								mode="single"
								selected={startDate}
								onSelect={setStartDate}
								onDayKeyPress={() => setPickerOpen(false)}
								onDayClick={() => setPickerOpen(false)}
								initialFocus
							/>
						</PopoverContent>
					</Popover>
				</div>
				<div className="flex justify-between items-center mb-4">
					<p>End Date:</p>
					<Popover open={secondPickerOpen} onOpenChange={setSecondPickerOpen}>
						<PopoverTrigger asChild>
							<Button variant={"outline"} className="pl-3 text-left font-normal">
								{format(endDate!, "PPP")}
								<CalendarIcon className="ml-6 h-4 w-4 opacity-50" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-auto p-0" align="start">
							<Calendar
								mode="single"
								selected={endDate}
								onSelect={setEndDate}
								onDayKeyPress={() => setSecondPickerOpen(false)}
								onDayClick={() => setSecondPickerOpen(false)}
								initialFocus
							/>
						</PopoverContent>
					</Popover>
				</div>
			</div>
			<div className="bg-white pb-4 border rounded-md shadow-sm">
				{upcomingEvents.isLoading ? (
					<div className="grid gap-4 pt-4">
						{Array.from({ length: 10 }).map((_, num) => (
							<Skeleton key={num} className="h-12 w-full" />
						))}
					</div>
				) : upcomingEvents.data ? (
					<DataTable data={upcomingEvents.data} columns={Columns(displayEvent)} />
				) : (
					<p>No Upcoming Events</p>
				)}
			</div>
		</div>
	);
};

export default UpcomingEvents;

const Columns = (
	displayEvent: (event: UpcomingEvent) => void
): ColumnDef<UpcomingEvent>[] => {
	return [
		{
			accessorKey: "actionid",
			header: ({ column }) => <ColumnHeader column={column} title="Event ID" />,
			cell: ({ row }) => (
				<Button
					variant="link"
					className="px-2 py-1"
					onClick={() => displayEvent(row.original)}
				>
					{row.getValue("actionid")}
				</Button>
			),
		},
		{
			accessorKey: "actiontype",
			header: ({ column }) => <ColumnHeader column={column} title="Action Type" />,
		},
		{
			accessorKey: "actiondate",
			header: ({ column }) => <ColumnHeader column={column} title="Action Date" />,
			cell: ({ row }) => convertDateToText(row.getValue("actiondate")),
		},
		{
			accessorKey: "petitioner",
			header: ({ column }) => <ColumnHeader column={column} title="Petitioner" />,
		},
		{
			accessorKey: "lawfirm",
			header: ({ column }) => <ColumnHeader column={column} title="Law Firm" />,
		},
		{
			accessorKey: "caseno",
			header: ({ column }) => <ColumnHeader column={column} title="Case No" />,
			cell: ({ row }) => (
				<div className="flex items-center">
					<NavLink to={`/case/${row.original.caseid}`}>
						<Button variant="link" className="px-2 py-1">
							{row.getValue("caseno")}
						</Button>
					</NavLink>
				</div>
			),
		},
	];
};
